import { cn } from "@theprelude/core";
import { forwardRef, HTMLAttributes, InputHTMLAttributes, useId } from "react";

export type ToggleButtonProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onClick"
> &
  Pick<HTMLAttributes<HTMLLabelElement>, "onClick">;

export const ToggleButton = forwardRef<HTMLInputElement, ToggleButtonProps>(
  function ToggleButton({ className, onClick, ...props }, ref) {
    const { checked, value, disabled } = props;
    const isChecked = Boolean(checked || value);

    return (
      <label
        onClick={onClick}
        className={cn(
          "flex cursor-pointer items-center justify-center",
          disabled && "cursor-not-allowed",
          className,
        )}
      >
        <input
          {...props}
          ref={ref}
          type="checkbox"
          className="h-0 w-0 appearance-none focus:outline-none"
        />
        <div
          className={cn(
            "relative inline-flex h-4 w-8 items-center rounded-2xl border border-secondary-foreground border-solid bg-surface-background p-0.5",
            isChecked && "border-status-green bg-status-green",
            disabled && "border-tertiary-foreground bg-surface-background",
          )}
        >
          <div
            className={cn(
              "absolute size-3 translate-x-0 transform rounded-full border border-secondary-foreground bg-secondary-foreground transition-transform ease-in-out",
              isChecked &&
                "translate-x-3.5 border-surface-background bg-surface-background",
              disabled && "border-tertiary-foreground bg-tertiary-foreground",
            )}
          ></div>
        </div>
      </label>
    );
  },
);

export interface LabeledToggleButtonProps extends ToggleButtonProps {
  label: string;
}

export const LabeledToggleButton = forwardRef<
  HTMLInputElement,
  LabeledToggleButtonProps
>(function LabeledToggleButton({ label, ...props }, ref) {
  const defaultId = useId();
  const id = props.id || defaultId;

  return (
    <div className="flex items-center">
      <ToggleButton ref={ref} {...props} id={id} />
      <label
        htmlFor={id}
        className={cn(
          "cursor-pointer pl-2",
          props.disabled && "cursor-not-allowed text-secondary-foreground",
        )}
      >
        {label}
      </label>
    </div>
  );
});
